import React, { useState, useEffect, useRef } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, useTheme } from '@mui/material';
import { Menu as MenuIcon, AccountCircle } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../provider/authProvider';
import AdminSidebar from '../../Components/Admin/admin_sidebar';
import AdminDashboard from '../../Components/Admin/admin_dashboard';
import AdminUserManagement from '../../Components/Admin/admin_user_management';
import AdminStrategyManagement from '../../Components/Admin/AdminStrategyManagement';
import AdminQAIUserManagement from '../../Components/Admin/AdminQAIUserManagement';
import './admin_main_page.css';

const AdminMainPage = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'dashboard');
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const navigate = useNavigate();
    const { clearToken } = useAuth();
    const theme = useTheme();
    const userManagementRef = useRef(null);
    const strategyManagementRef = useRef(null);

    // Update active tab if location state changes
    useEffect(() => {
        if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);
        }
    }, [location.state]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    const handleSidebarToggle = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleLogout = () => {
        clearToken();
        navigate('/login', { replace: true });
    };

    const handleCreateUser = () => {
        if (userManagementRef.current && userManagementRef.current.openCreateUserModal) {
            userManagementRef.current.openCreateUserModal();
        }
    };

    const handleCreateStrategy = () => {
        if (strategyManagementRef.current && strategyManagementRef.current.openCreateStrategyModal) {
            strategyManagementRef.current.openCreateStrategyModal();
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return <AdminDashboard />;
            case 'user-management':
                return <AdminUserManagement ref={userManagementRef} />;
            case 'strategy-management':
                return <AdminStrategyManagement ref={strategyManagementRef} />;
            case 'qai-users':
                return <AdminQAIUserManagement />;
            case 'qai-transactions':
                return (
                    <Box sx={{ p: 3, background: '#191b20', minHeight: '100vh' }}>
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                            Token Transactions
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            Transaction history and analytics will be available here.
                        </Typography>
                    </Box>
                );
            case 'settings':
                return (
                    <Box className="admin-settings-placeholder">
                        <Typography variant="h4" className="admin-settings-title">
                            Settings
                        </Typography>
                        <Typography variant="body1" className="admin-settings-description">
                            System settings and configuration options will be available here.
                        </Typography>
                    </Box>
                );
            default:
                return <AdminDashboard />;
        }
    };


    return (
        <Box className="admin-main-container" sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            minHeight: '100vh'
        }}>
            {/* Sidebar */}
            <AdminSidebar
                activeTab={activeTab}
                onTabChange={handleTabChange}
                onLogout={handleLogout}
                onCreateUser={handleCreateUser}
                onCreateStrategy={handleCreateStrategy}
            />

            {/* Main Content Area */}
            <Box className={`admin-content ${!sidebarOpen ? 'sidebar-collapsed' : ''}`} sx={{
                flex: 1,
                marginLeft: { xs: 0, sm: '240px', md: '280px' },
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Top App Bar */}
                <AppBar 
                    position="static" 
                    elevation={0}
                    className="admin-app-bar"
                    sx={{
                        display: { xs: 'block', sm: 'none' }
                    }}
                >
                    <Toolbar className="admin-toolbar" sx={{
                        minHeight: { xs: '48px', sm: '56px' },
                        padding: { xs: '0 8px', sm: '0 16px' }
                    }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={handleSidebarToggle}
                            className="admin-menu-button"
                            sx={{
                                display: { xs: 'block', sm: 'none' }
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        
                        <Typography
                            variant="h6"
                            component="div"
                            className="admin-page-title"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.25rem' },
                                fontWeight: { xs: 500, sm: 600 }
                            }}
                        >
                            {activeTab === 'dashboard' && 'Dashboard'}
                            {activeTab === 'user-management' && 'User Management'}
                            {activeTab === 'settings' && 'Settings'}
                        </Typography>

                        <Box className="admin-toolbar-spacer" />

                        <Box className="admin-user-info" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 2 }
                        }}>
                            <Typography variant="body2" className="admin-panel-label" sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                display: { xs: 'none', sm: 'block' }
                            }}>
                                Admin Panel
                            </Typography>
                            <IconButton className="admin-user-button" sx={{
                                padding: { xs: '4px', sm: '8px' }
                            }}>
                                <AccountCircle sx={{
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                }} />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </AppBar>

                {/* Page Content */}
                <Box className="admin-page-content" sx={{
                    flex: 1,
                    padding: { xs: '8px', sm: '16px', md: '24px' },
                    overflow: 'auto'
                }}>
                    {renderContent()}
                </Box>
            </Box>
        </Box>
    );
};

export default AdminMainPage;
